/**
 * Offline Storage Service - Advanced offline data management with sync capabilities
 * Handles local storage, sync queues, conflict resolution, and data persistence
 */

import Dexie, { Table } from 'dexie';

// Database schema interfaces
export interface OfflineCustomer {
  id?: number;
  serverId?: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  notes?: string;
  status: 'active' | 'inactive' | 'prospect';
  createdAt: string;
  updatedAt: string;
  lastSyncAt?: string;
  isDirty: boolean;
  isDeleted: boolean;
}

export interface OfflineProject {
  id?: number;
  serverId?: string;
  customerId: string;
  name: string;
  description?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  budget?: number;
  startDate?: string;
  endDate?: string;
  createdAt: string;
  updatedAt: string;
  lastSyncAt?: string;
  isDirty: boolean;
  isDeleted: boolean;
}

export interface OfflineAppointment {
  id?: number;
  serverId?: string;
  customerId: string;
  projectId?: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  location?: string;
  status: 'scheduled' | 'completed' | 'cancelled' | 'rescheduled';
  createdAt: string;
  updatedAt: string;
  lastSyncAt?: string;
  isDirty: boolean;
  isDeleted: boolean;
}

export interface SyncOperation {
  id?: number;
  entityType: 'customer' | 'project' | 'appointment';
  entityId: string;
  operation: 'create' | 'update' | 'delete';
  data: any;
  timestamp: string;
  retryCount: number;
  maxRetries: number;
  lastError?: string;
  priority: number;
}

export interface OfflineSettings {
  id?: number;
  key: string;
  value: any;
  updatedAt: string;
}

// Conflict resolution strategies
export type ConflictResolutionStrategy = 
  | 'server-wins' 
  | 'client-wins' 
  | 'latest-wins' 
  | 'manual';

export interface ConflictInfo {
  entityType: string;
  entityId: string;
  serverData: any;
  localData: any;
  strategy: ConflictResolutionStrategy;
}

// Database class
class OfflineDatabase extends Dexie {
  customers!: Table<OfflineCustomer>;
  projects!: Table<OfflineProject>;
  appointments!: Table<OfflineAppointment>;
  syncQueue!: Table<SyncOperation>;
  settings!: Table<OfflineSettings>;

  constructor() {
    super('CustomDesignManagerDB');
    
    this.version(1).stores({
      customers: '++id, serverId, email, status, createdAt, updatedAt, isDirty, isDeleted',
      projects: '++id, serverId, customerId, status, priority, createdAt, updatedAt, isDirty, isDeleted',
      appointments: '++id, serverId, customerId, projectId, startTime, status, createdAt, updatedAt, isDirty, isDeleted',
      syncQueue: '++id, entityType, entityId, operation, timestamp, retryCount, priority',
      settings: '++id, key, updatedAt'
    });

    // Hooks for automatic timestamp updates
    this.customers.hook('creating', (primKey, obj, trans) => {
      obj.createdAt = obj.createdAt || new Date().toISOString();
      obj.updatedAt = new Date().toISOString();
      obj.isDirty = true;
      obj.isDeleted = false;
    });

    this.customers.hook('updating', (modifications: any, primKey, obj, trans) => {
      modifications.updatedAt = new Date().toISOString();
      modifications.isDirty = true;
    });

    this.projects.hook('creating', (primKey, obj, trans) => {
      obj.createdAt = obj.createdAt || new Date().toISOString();
      obj.updatedAt = new Date().toISOString();
      obj.isDirty = true;
      obj.isDeleted = false;
    });

    this.projects.hook('updating', (modifications: any, primKey, obj, trans) => {
      modifications.updatedAt = new Date().toISOString();
      modifications.isDirty = true;
    });

    this.appointments.hook('creating', (primKey, obj, trans) => {
      obj.createdAt = obj.createdAt || new Date().toISOString();
      obj.updatedAt = new Date().toISOString();
      obj.isDirty = true;
      obj.isDeleted = false;
    });

    this.appointments.hook('updating', (modifications: any, primKey, obj, trans) => {
      modifications.updatedAt = new Date().toISOString();
      modifications.isDirty = true;
    });
  }
}

class OfflineService {
  private db: OfflineDatabase;
  private syncInProgress = false;
  private syncCallbacks: Array<(status: string, progress?: number) => void> = [];
  private conflictCallbacks: Array<(conflicts: ConflictInfo[]) => void> = [];
  private isInitialized = false;

  constructor() {
    this.db = new OfflineDatabase();
  }

  /**
   * Initialize offline service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      await this.db.open();
      
      // Setup periodic sync (every 5 minutes when online)
      this.setupPeriodicSync();
      
      // Setup network change handlers
      this.setupNetworkHandlers();
      
      this.isInitialized = true;
      console.log('🗄️ Offline Service initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize offline service:', error);
      throw error;
    }
  }

  // === CUSTOMER OPERATIONS ===

  /**
   * Get all customers (online/offline)
   */
  async getCustomers(includeDeleted = false): Promise<OfflineCustomer[]> {
    const query = includeDeleted 
      ? this.db.customers.toArray()
      : this.db.customers.where('isDeleted').equals(0).toArray();
    
    return await query;
  }

  /**
   * Get customer by ID
   */
  async getCustomer(id: string): Promise<OfflineCustomer | undefined> {
    // Try by serverId first, then by local id
    let customer = await this.db.customers.where('serverId').equals(id).first();
    if (!customer) {
      const localId = parseInt(id);
      if (!isNaN(localId)) {
        customer = await this.db.customers.get(localId);
      }
    }
    return customer && !customer.isDeleted ? customer : undefined;
  }

  /**
   * Create customer (offline-first)
   */
  async createCustomer(customerData: Omit<OfflineCustomer, 'id' | 'createdAt' | 'updatedAt' | 'isDirty' | 'isDeleted'>): Promise<OfflineCustomer> {
    const customer: OfflineCustomer = {
      ...customerData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isDirty: true,
      isDeleted: false
    };

    const id = await this.db.customers.add(customer);
    const createdCustomer = await this.db.customers.get(id);
    
    if (createdCustomer) {
      // Add to sync queue
      await this.addToSyncQueue('customer', id.toString(), 'create', createdCustomer);
      
      // Trigger sync if online
      if (navigator.onLine) {
        this.triggerSync();
      }
    }
    
    return createdCustomer!;
  }

  /**
   * Update customer (offline-first)
   */
  async updateCustomer(id: string, updates: Partial<OfflineCustomer>): Promise<OfflineCustomer | undefined> {
    const customer = await this.getCustomer(id);
    if (!customer) return undefined;

    const localId = customer.id!;
    await this.db.customers.update(localId, {
      ...updates,
      updatedAt: new Date().toISOString(),
      isDirty: true
    });

    const updatedCustomer = await this.db.customers.get(localId);
    
    if (updatedCustomer) {
      // Add to sync queue
      await this.addToSyncQueue('customer', localId.toString(), 'update', updatedCustomer);
      
      // Trigger sync if online
      if (navigator.onLine) {
        this.triggerSync();
      }
    }

    return updatedCustomer;
  }

  /**
   * Delete customer (soft delete, offline-first)
   */
  async deleteCustomer(id: string): Promise<boolean> {
    const customer = await this.getCustomer(id);
    if (!customer) return false;

    const localId = customer.id!;
    await this.db.customers.update(localId, {
      isDeleted: true,
      updatedAt: new Date().toISOString(),
      isDirty: true
    });

    // Add to sync queue
    await this.addToSyncQueue('customer', localId.toString(), 'delete', { id: customer.serverId || localId.toString() });
    
    // Trigger sync if online
    if (navigator.onLine) {
      this.triggerSync();
    }

    return true;
  }

  // === PROJECT OPERATIONS ===

  /**
   * Get all projects (online/offline)
   */
  async getProjects(includeDeleted = false): Promise<OfflineProject[]> {
    const query = includeDeleted 
      ? this.db.projects.toArray()
      : this.db.projects.where('isDeleted').equals(0).toArray();
    
    return await query;
  }

  /**
   * Get projects by customer ID
   */
  async getProjectsByCustomer(customerId: string): Promise<OfflineProject[]> {
    return await this.db.projects
      .where('customerId').equals(customerId)
      .and(project => !project.isDeleted)
      .toArray();
  }

  /**
   * Create project (offline-first)
   */
  async createProject(projectData: Omit<OfflineProject, 'id' | 'createdAt' | 'updatedAt' | 'isDirty' | 'isDeleted'>): Promise<OfflineProject> {
    const project: OfflineProject = {
      ...projectData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isDirty: true,
      isDeleted: false
    };

    const id = await this.db.projects.add(project);
    const createdProject = await this.db.projects.get(id);
    
    if (createdProject) {
      await this.addToSyncQueue('project', id.toString(), 'create', createdProject);
      if (navigator.onLine) this.triggerSync();
    }
    
    return createdProject!;
  }

  // === APPOINTMENT OPERATIONS ===

  /**
   * Get all appointments (online/offline)
   */
  async getAppointments(includeDeleted = false): Promise<OfflineAppointment[]> {
    const query = includeDeleted 
      ? this.db.appointments.toArray()
      : this.db.appointments.where('isDeleted').equals(0).toArray();
    
    return await query;
  }

  /**
   * Get appointments by date range
   */
  async getAppointmentsByDateRange(startDate: string, endDate: string): Promise<OfflineAppointment[]> {
    return await this.db.appointments
      .where('startTime').between(startDate, endDate, true, true)
      .and(appointment => !appointment.isDeleted)
      .toArray();
  }

  // === SYNC OPERATIONS ===

  /**
   * Add operation to sync queue
   */
  private async addToSyncQueue(
    entityType: SyncOperation['entityType'],
    entityId: string,
    operation: SyncOperation['operation'],
    data: any,
    priority = 1
  ): Promise<void> {
    const syncOp: SyncOperation = {
      entityType,
      entityId,
      operation,
      data,
      timestamp: new Date().toISOString(),
      retryCount: 0,
      maxRetries: 3,
      priority
    };

    await this.db.syncQueue.add(syncOp);
  }

  /**
   * Process sync queue
   */
  async syncWithServer(apiService: any): Promise<void> {
    if (this.syncInProgress || !navigator.onLine) {
      console.log('🔄 Sync already in progress or offline');
      return;
    }

    this.syncInProgress = true;
    this.notifySyncCallbacks('started');

    try {
      // Get all pending sync operations, ordered by priority and timestamp
      const operations = await this.db.syncQueue
        .orderBy('[priority, timestamp]')
        .toArray();

      if (operations.length === 0) {
        this.notifySyncCallbacks('completed', 100);
        return;
      }

      console.log(`🔄 Processing ${operations.length} sync operations`);

      let processed = 0;
      const conflicts: ConflictInfo[] = [];

      for (const operation of operations) {
        try {
          await this.processSyncOperation(operation, apiService);
          await this.db.syncQueue.delete(operation.id!);
          processed++;
          
          const progress = (processed / operations.length) * 100;
          this.notifySyncCallbacks('progress', progress);
          
        } catch (error) {
          console.error(`❌ Sync operation failed:`, operation, error);
          
          // Handle conflicts
          if (this.isConflictError(error)) {
            conflicts.push(await this.createConflictInfo(operation, error));
          }
          
          // Increment retry count
          await this.db.syncQueue.update(operation.id!, {
            retryCount: operation.retryCount + 1,
            lastError: (error as Error).message
          });

          // Remove if max retries reached
          if (operation.retryCount >= operation.maxRetries) {
            await this.db.syncQueue.delete(operation.id!);
            console.error(`❌ Max retries reached for operation:`, operation);
          }
        }
      }

      // Handle conflicts
      if (conflicts.length > 0) {
        this.notifyConflictCallbacks(conflicts);
      }

      this.notifySyncCallbacks('completed', 100);
      console.log(`✅ Sync completed: ${processed}/${operations.length} operations processed`);

    } catch (error) {
      console.error('❌ Sync failed:', error);
      this.notifySyncCallbacks('failed');
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Process individual sync operation
   */
  private async processSyncOperation(operation: SyncOperation, apiService: any): Promise<void> {
    const { entityType, entityId, operation: op, data } = operation;

    switch (entityType) {
      case 'customer':
        if (op === 'create') {
          const response = await apiService.createCustomer(data);
          // Update local record with server ID
          await this.db.customers.update(parseInt(entityId), {
            serverId: response.id,
            isDirty: false,
            lastSyncAt: new Date().toISOString()
          });
        } else if (op === 'update') {
          await apiService.updateCustomer(data.serverId || entityId, data);
          await this.db.customers.update(parseInt(entityId), {
            isDirty: false,
            lastSyncAt: new Date().toISOString()
          });
        } else if (op === 'delete') {
          await apiService.deleteCustomer(data.id);
          await this.db.customers.delete(parseInt(entityId));
        }
        break;

      case 'project':
        // Similar implementation for projects
        break;

      case 'appointment':
        // Similar implementation for appointments
        break;
    }
  }

  /**
   * Sync data from server to local
   */
  async syncFromServer(apiService: any): Promise<void> {
    try {
      console.log('📥 Syncing data from server...');

      // Sync customers
      const customers = await apiService.getCustomers();
      for (const customer of customers) {
        await this.upsertCustomerFromServer(customer);
      }

      // Sync projects
      const projects = await apiService.getProjects();
      for (const project of projects) {
        await this.upsertProjectFromServer(project);
      }

      // Sync appointments
      const appointments = await apiService.getAppointments();
      for (const appointment of appointments) {
        await this.upsertAppointmentFromServer(appointment);
      }

      console.log('✅ Server sync completed');
    } catch (error) {
      console.error('❌ Server sync failed:', error);
      throw error;
    }
  }

  /**
   * Upsert customer from server data
   */
  private async upsertCustomerFromServer(serverCustomer: any): Promise<void> {
    const existingCustomer = await this.db.customers.where('serverId').equals(serverCustomer.id).first();

    if (existingCustomer) {
      // Check for conflicts
      if (existingCustomer.isDirty && existingCustomer.updatedAt > serverCustomer.updated_at) {
        // Local changes are newer - conflict detected
        console.warn('⚠️ Customer conflict detected:', existingCustomer.id);
        return;
      }

      // Update existing customer
      await this.db.customers.update(existingCustomer.id!, {
        name: serverCustomer.name,
        email: serverCustomer.email,
        phone: serverCustomer.phone,
        address: serverCustomer.address,
        notes: serverCustomer.notes,
        status: serverCustomer.status,
        updatedAt: serverCustomer.updated_at,
        lastSyncAt: new Date().toISOString(),
        isDirty: false
      });
    } else {
      // Create new customer
      const customer: OfflineCustomer = {
        serverId: serverCustomer.id,
        name: serverCustomer.name,
        email: serverCustomer.email,
        phone: serverCustomer.phone,
        address: serverCustomer.address,
        notes: serverCustomer.notes,
        status: serverCustomer.status,
        createdAt: serverCustomer.created_at,
        updatedAt: serverCustomer.updated_at,
        lastSyncAt: new Date().toISOString(),
        isDirty: false,
        isDeleted: false
      };

      await this.db.customers.add(customer);
    }
  }

  /**
   * Similar methods for projects and appointments...
   */
  private async upsertProjectFromServer(serverProject: any): Promise<void> {
    // Implementation similar to upsertCustomerFromServer
  }

  private async upsertAppointmentFromServer(serverAppointment: any): Promise<void> {
    // Implementation similar to upsertCustomerFromServer
  }

  // === UTILITY METHODS ===

  /**
   * Get pending sync operations count
   */
  async getPendingSyncCount(): Promise<number> {
    return await this.db.syncQueue.count();
  }

  /**
   * Get database statistics
   */
  async getStats() {
    const [customers, projects, appointments, pendingSync] = await Promise.all([
      this.db.customers.where('isDeleted').equals(0).count(),
      this.db.projects.where('isDeleted').equals(0).count(),
      this.db.appointments.where('isDeleted').equals(0).count(),
      this.db.syncQueue.count()
    ]);

    return {
      customers,
      projects,
      appointments,
      pendingSync,
      totalRecords: customers + projects + appointments
    };
  }

  /**
   * Clear all data (for testing/reset)
   */
  async clearAllData(): Promise<void> {
    await this.db.transaction('rw', this.db.customers, this.db.projects, this.db.appointments, this.db.syncQueue, async () => {
      await this.db.customers.clear();
      await this.db.projects.clear();
      await this.db.appointments.clear();
      await this.db.syncQueue.clear();
    });
    
    console.log('🗑️ All offline data cleared');
  }

  // === EVENT HANDLERS ===

  /**
   * Register sync status callback
   */
  onSyncStatus(callback: (status: string, progress?: number) => void): () => void {
    this.syncCallbacks.push(callback);
    return () => {
      const index = this.syncCallbacks.indexOf(callback);
      if (index > -1) this.syncCallbacks.splice(index, 1);
    };
  }

  /**
   * Register conflict callback
   */
  onConflict(callback: (conflicts: ConflictInfo[]) => void): () => void {
    this.conflictCallbacks.push(callback);
    return () => {
      const index = this.conflictCallbacks.indexOf(callback);
      if (index > -1) this.conflictCallbacks.splice(index, 1);
    };
  }

  // === PRIVATE METHODS ===

  private setupPeriodicSync(): void {
    // Sync every 5 minutes when online
    setInterval(() => {
      if (navigator.onLine && !this.syncInProgress) {
        this.triggerSync();
      }
    }, 5 * 60 * 1000);
  }

  private setupNetworkHandlers(): void {
    window.addEventListener('online', () => {
      console.log('🌐 Back online - triggering sync');
      this.triggerSync();
    });
  }

  private async triggerSync(): Promise<void> {
    // This would need to be connected to your API service
    console.log('🔄 Triggering background sync...');
    // Implementation depends on your API service structure
  }

  private notifySyncCallbacks(status: string, progress?: number): void {
    this.syncCallbacks.forEach(callback => {
      try {
        callback(status, progress);
      } catch (error) {
        console.error('❌ Sync callback error:', error);
      }
    });
  }

  private notifyConflictCallbacks(conflicts: ConflictInfo[]): void {
    this.conflictCallbacks.forEach(callback => {
      try {
        callback(conflicts);
      } catch (error) {
        console.error('❌ Conflict callback error:', error);
      }
    });
  }

  private isConflictError(error: any): boolean {
    return error?.status === 409 || error?.message?.includes('conflict');
  }

  private async createConflictInfo(operation: SyncOperation, error: any): Promise<ConflictInfo> {
    return {
      entityType: operation.entityType,
      entityId: operation.entityId,
      serverData: error.serverData || {},
      localData: operation.data,
      strategy: 'manual'
    };
  }
}

// Export singleton instance
export const offlineService = new OfflineService();
export default offlineService;